/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { useRelayEnvironment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import { SessionTracker } from '../../../common/analytics/SessionTracker';
import { HELLO_CLOUD_ID, PUG_CLOUD_ID } from '../../../config';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { CollectFeedback } from '../../components/CollectFeedback';
import { LoadingView } from '../../components/LoadingView';
import {
	accountIdAtom,
	availableSitesAtom,
	cloudIdAtom,
	orgIdAtom,
	relayEnvironmentAtom,
} from '../../state';

import { LoggedInView } from './components/LoggedInView';
import { LoggedInViewV2 } from './components/LoggedInViewV2';
import { LoggedOutView } from './components/LoggedOutView';

/**
 * Gating component that resolves the user's `cloudId` and primes the
 * shared atoms before `LoggedInView` mounts.
 *
 * It reads `availableSitesAtom` (which suspends until the App Switcher
 * `available-products` request resolves), then synchronously hydrates
 * `accountIdAtom`, `cloudIdAtom`, and `relayEnvironmentAtom` so that
 * downstream async atoms — `KnowledgeSourcesAtom`,
 * `CollaborationUsersAtom`, etc. — fire exactly once with the correct
 * tenant. While the App Switcher request is in flight, the parent
 * `<Suspense>` boundary in `Home` shows the loading fallback.
 *
 * If the request fails or returns no usable site, `cloudIdAtom` stays
 * `null` and the cloudId-dependent atoms remain idle (no network
 * calls), keeping the page functional in a degraded state.
 */
function LoggedInGate(): JSX.Element {
	const { identity } = useAtlassianUserContext();
	const environment = useRelayEnvironment();
	const { data: sites } = useAtomValue(availableSitesAtom);

	// Pick the first site that has a cloudId. If none do, leave it
	// `null` — downstream atoms guard against `null` and will stay
	// idle, while the rest of the page can still render.
	const resolvedSite = useMemo(() => {
		// Priority Order
		// 1. Hello
		// 2. Pug
		// 3. Via Identity
		// 4. Plain first

		// look for atlassian home sites
		const atlassianHello = sites.find((x) => x.cloudId === HELLO_CLOUD_ID);
		const atlassianPUG = sites.find((x) => x.cloudId === PUG_CLOUD_ID);

		// the first one we pick will the one that comes as part of identity.orgId
		const firstViaIdentity = sites.find((x) => x.org?.id === identity.orgId);

		// Plain first
		const first = sites.find((x) => Boolean(x.cloudId));

		const final = atlassianHello ?? atlassianPUG ?? firstViaIdentity ?? first;
		// eslint-disable-next-line no-console
		console.log(`Picked site ${final?.displayName} (${final?.cloudId})`);
		return final;
	}, [sites, identity]);

	useHydrateAtoms([
		[accountIdAtom, identity.accountId],
		[cloudIdAtom, resolvedSite?.cloudId ?? null],
		[orgIdAtom, resolvedSite?.org?.id ?? null],
		[relayEnvironmentAtom, environment],
	]);

	if (fg('twg_microsite_exploration_v2')) {
		return <LoggedInViewV2 />;
	}
	return <LoggedInView />;
}

export default function Home(): React.JSX.Element {
	const { loading, isAuthed, identity } = useAtlassianUserContext();

	if (loading) {
		return <LoadingView />;
	}

	if (!isAuthed) {
		return (
			<React.Fragment>
				<SessionTracker authState="loggedOut" />
				<LoggedOutView />
				<CollectFeedback
					atlassianAccountId=""
					entrypointId="5cc48fe0-977e-45ce-9c7d-bbd5ee4a00ce"
					name=""
					url={window.location.href}
				/>
			</React.Fragment>
		);
	}

	// TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback
	return (
		<React.Fragment>
			<SessionTracker authState="loggedIn" />
			<Suspense fallback={<LoadingView />}>
				<LoggedInGate />
			</Suspense>
			<CollectFeedback
				atlassianAccountId={identity.accountId}
				entrypointId="5cc48fe0-977e-45ce-9c7d-bbd5ee4a00ce"
				name={identity.name}
				url={window.location.href}
			/>
		</React.Fragment>
	);
}
